import type { BasePatchFrame, BasePatchOp } from './base-patch';

const upper = (value: unknown): string => String(value ?? '').toUpperCase();
const DEFINITION_TABLES = new Set(['items', 'item_layer_types']);
const keyOf = (op: BasePatchOp): string => `${op.table}:${upper(op.row_id)}`;
/** The relay's per-host marker: the definition keys ft_entity_base_frame_append skipped for that host because an earlier host on the
 *  same channel already carried them. Not part of the op contract; read and removed here. */
const skipped = (op: BasePatchOp): string[] => {
    const marker = (op as { shared_definitions?: unknown }).shared_definitions;
    return Array.isArray(marker) ? marker.filter((key): key is string => typeof key === 'string') : [];
};

/** The relay delivers each authored definition (items / item_layer_types) ONCE per channel frame, under the first host that carries
 *  it (ft_entity_base_frame_append: a ten-member pull used to repeat a ~250 KB closure eleven times in one 2.76 MB frame); a later host
 *  that also carried it records the skipped keys on its first op (`shared_definitions`). Restore EXACTLY that host's own closure: each
 *  named definition is re-stamped into that group with the group's identity and revision, so the per-group definition comparison and the
 *  reference guards keep their meaning; a host receives nothing it did not carry before the dedup, and a frame without markers — one
 *  the relay deduplicated nothing in, or a pre-dedup frame — is returned as is. Row ops, their dependency revisions and catalog_inputs
 *  stay where the relay put them. */
export function withSharedDefinitionOps(frame: BasePatchFrame): BasePatchFrame {
    if (!frame.ops.some(op => skipped(op).length)) return frame;
    const definitions = new Map<string, BasePatchOp>();
    for (const op of frame.ops) if (DEFINITION_TABLES.has(op.table) && !definitions.has(keyOf(op))) definitions.set(keyOf(op), op);
    const groups = new Map<string, BasePatchOp[]>();
    for (const op of frame.ops) {
        const id = upper(op.entity_id), group = groups.get(id) ?? [];
        group.push(op); groups.set(id, group);
    }
    const ops: BasePatchOp[] = [];
    for (const group of groups.values()) {
        const wanted = new Set(group.flatMap(skipped)), carried = new Set(group.filter(op => DEFINITION_TABLES.has(op.table)).map(keyOf));
        const revision = Math.max(...group.map(op => op.base_rev));
        for (const op of group) {
            if (!skipped(op).length) { ops.push(op); continue; }
            const { shared_definitions: _marker, ...own } = op as BasePatchOp & { shared_definitions?: unknown };
            ops.push(own as BasePatchOp);
        }
        for (const key of wanted) {
            const op = definitions.get(key);
            if (!op || carried.has(key)) continue;
            carried.add(key);
            const { dependency_revs: _dependencies, catalog_inputs: _inputs, shared_definitions: _marker, ...shared } = op as BasePatchOp & { shared_definitions?: unknown };
            ops.push({ ...shared, entity_id: group[0]!.entity_id, base_rev: revision } as BasePatchOp);
        }
    }
    return { ...frame, ops };
}
